import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components';
import { Link } from 'react-router-dom';
import {
  Users,
  MapPin,
  Search,
  Star,
  Briefcase
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';

interface Professional {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  profile_photo?: string;
  location?: string;
  specializations?: string[];
  experience_years?: number;
  hourly_rate?: number;
  availability_status?: string;
  bio?: string;
  rating?: number;
  completed_projects?: number;
}

const ProfessionalListPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // Fetch professionals
  const { data: professionals, isLoading } = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => {
      const response = await api.get('/professionals');
      return response.data.data as Professional[];
    },
    enabled: !!user,
  });

  const filteredProfessionals = professionals?.filter(professional => {
    const matchesSearch = professional.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         professional.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         professional.specializations?.some(spec => 
                           spec.toLowerCase().includes(searchTerm.toLowerCase())
                         ) ||
                         professional.bio?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = !locationFilter || 
                           professional.location?.toLowerCase().includes(locationFilter.toLowerCase());

    return matchesSearch && matchesLocation;
  }) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('profList.title')}</h1>
        <p className="mt-1 text-gray-600">{t('profList.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('profList.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="w-full sm:w-64">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('profList.locationPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results count */}
      {!isLoading && (
        <div className="mb-4 text-sm text-gray-600">
          {t('profList.found', { count: filteredProfessionals.length })}
        </div>
      )}

      {/* Professionals List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{t('profList.loading')}</p>
        </div>
      ) : filteredProfessionals.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('profList.noneFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('profList.noneFoundHint')}</p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProfessionals.map((professional) => (
            <Card key={professional.id} className="hover:shadow-lg transition-shadow">
              <div className="text-center">
                {/* Profile Photo */}
                <div className="mb-4 flex justify-center">
                  <BelagomProfilePhoto
                    src={professional.profile_photo}
                    alt={`${professional.first_name} ${professional.last_name}`}
                    size={80}
                  />
                </div>

                {/* Name and Title */}
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {professional.first_name} {professional.last_name}
                  </h3>
                  {professional.specializations && professional.specializations.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {professional.specializations.slice(0, 2).join(', ')}
                      {professional.specializations.length > 2 && '...'}
                    </p>
                  )}
                </div>

                {/* Location */}
                {professional.location && (
                  <div className="flex items-center justify-center text-sm text-gray-500 mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    {professional.location}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  {professional.experience_years && (
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{professional.experience_years}</div>
                      <div className="text-gray-500">{t('profList.yearsExp')}</div>
                    </div>
                  )}
                  {professional.completed_projects !== undefined && (
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{professional.completed_projects}</div>
                      <div className="text-gray-500">{t('profList.projects')}</div>
                    </div>
                  )}
                  {professional.hourly_rate && (
                    <div className="text-center col-span-2">
                      <div className="font-semibold text-gray-900">€{(Number(professional.hourly_rate) * 1.2).toFixed(2)}/uur</div>
                      <div className="text-gray-500">{t('profList.rate')}</div>
                    </div>
                  )}
                </div>

                {/* Rating */}
                {professional.rating && (
                  <div className="flex items-center justify-center mb-4">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(professional.rating!)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-1 text-sm text-gray-600">
                        ({professional.rating.toFixed(1)})
                      </span>
                    </div>
                  </div>
                )}

                {/* Bio preview */}
                {professional.bio && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {professional.bio}
                  </p>
                )}

                {/* Availability Status */}
                {professional.availability_status && (
                  <div className="mb-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      professional.availability_status === 'available'
                        ? 'bg-green-100 text-green-800'
                        : professional.availability_status === 'busy'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {professional.availability_status === 'available' && `✅ ${t('profList.available')}`}
                      {professional.availability_status === 'busy' && `⏳ ${t('profList.limited')}`}
                      {professional.availability_status === 'unavailable' && `❌ ${t('profList.unavailable')}`}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Link 
                    to={`/professionals/${professional.id}`}
                    className="flex-1"
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      {t('profList.profile')}
                    </Button>
                  </Link>
                  {user?.role === 'opdrachtgever' && (
                    <Button size="sm" className="flex-1">
                      <Briefcase className="w-4 h-4 mr-1" />
                      {t('profList.contact')}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfessionalListPage;
